// src/store.js
// console.log('VX: Store initializing');

import { createStore } from 'vuex';
import { auth, db, functions } from '@/firebase/config';
import { query, where, collection, getDocs, doc, onSnapshot, getDoc, documentId } from "firebase/firestore";
import router from "@/router";
import { get, set, del } from 'idb-keyval';
import { parseISO, format, addDays } from 'date-fns'
import getEventAccessLevel from '@/composables/getEventAccessLevel';
import { getAuth } from 'firebase/auth'
import useLocationsWithinDistance from '@/composables/useLocationsWithinDistance';
import useHaversine from '@/composables/calcDistanceHav';
import { httpsCallable } from 'firebase/functions'



const flattenForIndexedDB = (obj) => {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (err) {
    console.error('❌ flattenForIndexedDB failed:', err);
    return {}; // or [] based on context
  }
};

let pollInterval = null;

export default createStore({
  state: {
    user: null,
    isAppSleeping: false,
    curPlayerData: null,
    playerListenerUnsub: null,
    playerListenerStatus: 'idle',
    userOnboardingStatus: null,
    version: null,
    versionListenerUnsub: null,
    currentLocLat: null,
    currentLocLon: null,
    lastLat: null,
    lastLon: null,
    myLocationsRaw: [],
    myLocationsRawLoaded: false,
    myLocationsListenerUnsub: null,
    myPlayersRaw: [],
    myPlayersRawLoaded: false,
    myPlayersRawListenerUnsubs: [],
    myPlayers: [],
    myPlayersRawMap: {},
    myLocationsAdminRaw: [],
    myLocationsAdminRawLoaded: false,
    myLocationsAdminListenerUnsub: null,
    myLocations: [],
    myLocationsRawMap: {},
    myFriendsRaw: [],
    myFriendsRawLoaded: false,
    myFriendsListenerUnsub: null,
    myFavoritesRaw: [],
    myFavoritesRawLoaded: false,
    myFavoritesListenerUnsub: null,
    myEventsSummary: [],              // Array of { id, eventId, startDate, startTimeMil }
    myEventsSummaryLoaded: false,     // Flag for initial load completion
    myEventsSummaryListenerUnsub: null, // Unsubscribe function for the listener
    myEventsDetail: [],               // Array of detailed event objects
    myEventsDetailLoaded: false,      // Flag for initial load completion
    myEventsDetailListenerUnsubs: [],
    eventCounts: [], // Array of { date, locationId, eventCount }
    eventCountsLoaded: false, // Tracks if event counts are loaded
    eventCountsUnsubscribe: null, // Stores the Firestore unsubscribe function
    eventCountsPrivate: [], // Array of private event counts
    eventCountsPrivateLoaded: false, // Tracks if private event counts are loaded
    eventCountsPrivateUnsubscribe: null, // Stores the Firestore unsubscribe function
    myPlayersLoaded: false,
    isFetchingMyPlayers: false,
    needsMyPlayersFetch: false,
    pendingMyPlayersFetchUserId: null,
    nearbyLocations: [],
    nearbyLat: null,
    nearbyLon: null,
    playerPassTier: null,
    playerPassTier: null,
    playerPassNFTID: null,
    playerPassLastClaimAt: null,
    playerPassLastClaimTx: null,
    platformFee: null,
    playerPassTokenBalance: null,
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    clearUser(state) {
      state.user = null;
    },
    setAppSleeping(state, isSleeping) {
      state.isAppSleeping = isSleeping;
    },
    setPlayerData(state, playerData) {
      state.curPlayerData = playerData;
    },
    clearPlayerData(state) {
      state.curPlayerData = null;
    },
    setPlayerListenerUnsub(state, unsub) {
      state.playerListenerUnsub = unsub;
    },
    setPlayerListenerStatus(state, status) {
      state.playerListenerStatus = status;
    },
    setUserOnboardingStatus(state, status) {
      state.userOnboardingStatus = status;
    },
    setVersion(state, version) {
      state.version = version;
      console.log(`Version now: ${version}`);
    },
    setVersionListenerUnsub(state, unsub) {
      state.versionListenerUnsub = unsub;
    },
    setCurrentLocation(state, { lat, lon }) {
      state.currentLocLat = lat;
      state.currentLocLon = lon;
      const playerId = state.curPlayerData?.id;
      if (playerId) {
        const updatePlayerPrivate = httpsCallable(functions, 'players-updatePlayerPrivate');
        updatePlayerPrivate({ playerId, fields: { lastLat: lat, lastLon: lon } })
          .then(() => console.log('lastLocation updated'))
          .catch(() => console.log('lastLocation NOT updated'));
      }
    },
    clearCurrentLocation(state) {
      state.currentLocLat = 0;
      state.currentLocLon = 0;
    },
    setLastLocation(state, { lastLat, lastLon }) {
      state.lastLat = lastLat;
      state.lastLon = lastLon;
    },
    clearLastLocation(state) {
      state.lastLat = null;
      state.lastLon = null;
    },
    setMyLocationsRaw(state, locations) {
      state.myLocationsRaw = locations;
    },
    setMyLocationsRawLoaded(state, loaded) {
      state.myLocationsRawLoaded = loaded;
    },
    setMyPlayersRaw(state, playersRaw) {
      state.myPlayersRaw = playersRaw;
    },
    setMyPlayersRawLoaded(state, value) {
      state.myPlayersRawLoaded = value;
    },
    setMyPlayers(state, players) {
      state.myPlayers = players;
    },
    setMyLocationsListenerUnsub(state, unsub) {
      state.myLocationsListenerUnsub = unsub;
    },
    setMyPlayersRawListenerUnsubs(state, unsubs) {
      state.myPlayersRawListenerUnsubs = unsubs;
    },
    updateMyPlayersRawMap(state, { locationId, playerIds, userId }) {
      const start = performance.now();

      const existingPlayers = new Set(state.myPlayersRaw);
      playerIds.forEach((playerId) => existingPlayers.add(playerId));
      const updatedPlayersRaw = Array.from(existingPlayers);

      state.myPlayersRaw = updatedPlayersRaw;

      // ✅ Build the lookup map from the already-loaded players
      const map = {};
      state.myPlayers.forEach((player) => {
        if (player.playerId) {
          map[player.playerId] = player;
        }
      });
      state.myPlayersRawMap = map;

      const key = userId ? `myPlayersRaw_${userId}` : 'myPlayersRaw';
      set(key, { data: updatedPlayersRaw })
        .then(() => console.log('💾'))
        .catch((err) => console.error('🔴 Error saving myPlayersRaw to IndexedDB:', err));

      if (!state.myPlayersRawLoaded) {
        state.myPlayersRawLoaded = true;
      }

      // console.log(`⏳ updateMyPlayersRawMap: ${(performance.now() - start).toFixed(2)} ms`);
    },

    setMyPlayersRawMap(state, map) {
      state.myPlayersRawMap = map;
      state.myPlayersRaw = [];
    },
    setMyLocationsAdminRaw(state, locations) {
      state.myLocationsAdminRaw = locations;
    },
    setMyLocationsAdminRawLoaded(state, loaded) {
      state.myLocationsAdminRawLoaded = loaded;
    },
    setMyLocationsAdminListenerUnsub(state, unsub) {
      state.myLocationsAdminListenerUnsub = unsub;
    },
    setMyLocations(state, locations) {
      state.myLocations = locations;
    },
    setMyLocationsRawMap(state, map) {
      state.myLocationsRawMap = map;
    },
    // new Friends
    setMyFriendsRaw(state, friends) {
      state.myFriendsRaw = friends;
    },
    setMyFriendsRawLoaded(state, loaded) {
      state.myFriendsRawLoaded = loaded;
    },
    setMyFriendsListenerUnsub(state, unsub) {
      state.myFriendsListenerUnsub = unsub;
    },
    // new Favorites
    setMyFavoritesRaw(state, favorites) {
      state.myFavoritesRaw = favorites;
    },
    setMyFavoritesRawLoaded(state, loaded) {
      state.myFavoritesRawLoaded = loaded;
    },
    setMyFavoritesListenerUnsub(state, unsub) {
      state.myFavoritesListenerUnsub = unsub;
    },
    updatePlayer(state, updatedPlayer) {
      const index = state.myPlayers.findIndex(p => p.playerId === updatedPlayer.playerId);
      if (index !== -1) {
        // Merge updated data into the existing player object
        state.myPlayers[index] = { ...state.myPlayers[index], ...updatedPlayer };
      } else {
        // If the player isn’t in the list yet, add it (optional, depending on your needs)
        state.myPlayers.push(updatedPlayer);
      }
    },
    setMyEventsSummary(state, events) {
      state.myEventsSummary = events;
    },
    setMyEventsSummaryLoaded(state, loaded) {
      state.myEventsSummaryLoaded = loaded;
    },
    setMyEventsSummaryListenerUnsub(state, unsub) {
      state.myEventsSummaryListenerUnsub = unsub;
    },
    setMyEventsDetail(state, events) {
      state.myEventsDetail = events;
    },
    setMyEventsDetailLoaded(state, loaded) {
      state.myEventsDetailLoaded = loaded;
    },
    setMyEventsDetailListenerUnsubs(state, unsubs) {
      state.myEventsDetailListenerUnsubs = unsubs;
    },
    // new for eventCounts
    setEventCounts(state, counts) {
      state.eventCounts = counts;
    },
    setEventCountsLoaded(state, loaded) {
      state.eventCountsLoaded = loaded;
    },
    setEventCountsUnsubscribe(state, unsub) {
      state.eventCountsUnsubscribe = unsub;
    },
    setEventCountsPrivate(state, counts) {
      state.eventCountsPrivate = counts;
    },
    setEventCountsPrivateLoaded(state, loaded) {
      state.eventCountsPrivateLoaded = loaded;
    },
    setEventCountsPrivateUnsubscribe(state, unsub) {
      state.eventCountsPrivateUnsubscribe = unsub;
    },
    setMyPlayersLoaded(state, value) {
      state.myPlayersLoaded = value;
    },
    setNearbyLocations(state, locations) {
      state.nearbyLocations = locations;
    },
    setNearbyLatLon(state, { lat, lon }) {
      state.nearbyLat = lat;
      state.nearbyLon = lon;
    },
    setPlayerPassTier(state, tier) {
      state.playerPassTier = tier || null
    },
    clearPlayerPassTier(state) {
      state.playerPassTier = null
    },
    setPlayerPassNFTID(state, tokenId) {
      state.playerPassNFTID = tokenId || null
    },
    clearPlayerPassNFTID(state) {
      state.playerPassNFTID = null
    },
    setLastClaimAt(state, ts) {
      state.playerPassLastClaimAt = ts || null
    },
    clearLastClaimAt(state) {
      state.playerPassLastClaimAt = null
    },
    setLastClaimTx(state, tx) {
      state.playerPassLastClaimTx = tx || null
    },
    clearLastClaimTx(state) {
      state.playerPassLastClaimTx = null
    },
    setPlatformFee(state, fee) {
      state.platformFee = typeof fee === 'number' ? fee : null
    },
    clearPlatformFee(state) {
      state.platformFee = null
    },
    setPlayerPassTokenBalance(state, balance) {
      state.playerPassTokenBalance = typeof balance === 'number' ? balance : null
    },
    clearPlayerPassTokenBalance(state) {
      state.playerPassTokenBalance = null
    },

  },
  actions: {
    trackAuthState({ commit, dispatch, state }) {
      const checkAuthState = async () => {
        if (auth.currentUser) {
          // console.log('🔒 checkAuthState')
          try {
            await auth.currentUser.reload();
            const user = auth.currentUser;
            if (user) {
              commit('setUser', user);
            } else {
              dispatch('handleLogout');
            }
          } catch (error) {
            if (error.code === 'auth/user-token-expired') {
              dispatch('handleLogout');
            }
          }
        }
      };

      auth.onAuthStateChanged(async (user) => {
        // console.log('🔒🔓 onAuthStateChanged')
        if (user) {
          const userId = user.uid; // Capture immediately
          // console.log('🙋 User is authenticated:', user.uid);
          commit('setUser', user);
          if (!pollInterval) {
            pollInterval = setInterval(checkAuthState, 10000);
          }
          // Load cached event data
          await dispatch('loadMyEventsFromCache');
          // stagger for Android devices
          if (/android/i.test(navigator.userAgent)) {
            setTimeout(() => dispatch('startMyEventsSummaryListener', userId), 0);
            setTimeout(() => dispatch('loadEventCountsFromCache'), 100);
            setTimeout(() => dispatch('loadEventCountsPrivateFromCache'), 200);
            setTimeout(() => dispatch('startMyLocationsListener', userId), 300);
            setTimeout(() => dispatch('startMyLocationsAdminListener', userId), 400);
            setTimeout(() => dispatch('startMyFriendsListener', userId), 500);
            setTimeout(() => dispatch('startMyFavoritesListener', userId), 600);
            setTimeout(() => dispatch('updateEventCountsListener'), 700);
            if (state.curPlayerData?.id) {
              setTimeout(() => dispatch('updateEventCountsPrivateListener'), 800);
            }
          } else {
            // Default behavior for iOS and desktop
            dispatch('startMyEventsSummaryListener', userId);
            dispatch('loadEventCountsFromCache');
            dispatch('loadEventCountsPrivateFromCache');
            dispatch('startMyLocationsListener', userId);
            dispatch('startMyLocationsAdminListener', userId);
            dispatch('startMyFriendsListener', userId);
            dispatch('startMyFavoritesListener', userId);
            dispatch('updateEventCountsListener');
            if (state.curPlayerData?.id) {
              dispatch('updateEventCountsPrivateListener');
            }
          }


        } else {
          // console.log('🔴 No user is authenticated');
          dispatch('handleLogout');
        }
      });
    },
    stopPlayerListener({ state, commit }) {
      if (state.playerListenerUnsub) {
        state.playerListenerUnsub();
        commit('setPlayerListenerUnsub', null);
        commit('clearPlayerData');
      }
    },
    handleLogout({ commit, dispatch, state }) {
      const userId = state.user?.uid;
      commit('clearUser');
      dispatch('stopMyEventsSummaryListener');
      dispatch('stopPlayerListener');
      dispatch('stopMyLocationsListener');
      dispatch('stopMyLocationsAdminListener');
      dispatch('stopMyPlayersRawListener', userId);
      dispatch('stopMyFriendsListener');
      dispatch('stopMyFavoritesListener');
      if (state.eventCountsUnsubscribe) {
        state.eventCountsUnsubscribe();
        commit('setEventCountsUnsubscribe', null);
        commit('setEventCounts', []);
        commit('setEventCountsLoaded', false);
      }
      if (state.eventCountsPrivateUnsubscribe) {
        state.eventCountsPrivateUnsubscribe();
        commit('setEventCountsPrivateUnsubscribe', null);
        commit('setEventCountsPrivate', []);
        commit('setEventCountsPrivateLoaded', false);
      }
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
      const currentPath = window.location.pathname;
      if (
        currentPath.startsWith('/register') ||
        currentPath === '/terms' ||
        currentPath === '/privacy'
      ) {
        router.push({ path: currentPath });
      } else {
        router.push({ name: 'home' });
      }
    },
    stopAuthPolling() {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    },
    async startPlayerListener({ commit, dispatch }, userId) {
      // this needs to be modified for regitration so we can increase security back to players read if isLoggedInAndRegistered();
      // but in order to do that we have to check for a firebase rights error when we try to read players or do a separate quick check
      // and if it fails due to security, then we continue with the else { console.error("No player document found with this userId"); etc
      try {
        commit('setPlayerListenerStatus', 'loading');
        const playersRef = collection(db, 'players');
        const q = query(playersRef, where("userId", "==", userId));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const docSnapshot = querySnapshot.docs[0];
          const playerDocRef = doc(db, 'players', docSnapshot.id);
          const playerPrivateDocRef = doc(db, 'playersPrivate', docSnapshot.id);

          const unsubscribePlayers = onSnapshot(
            playerDocRef,
            (doc) => {
              if (doc.exists()) {
                const playerData = doc.data();
                commit('setPlayerData', {
                  id: doc.id,
                  playerName: playerData.playerName,
                  skillSelf: playerData.skillSelf,
                  photoURL: playerData.photoURL,
                  homeLocation: playerData.homeLocation,
                  setupComplete: playerData.setupComplete ? playerData.setupComplete : false,
                  userId: playerData.userId,
                  homePage: playerData.homePage,
                  diamonds: playerData.diamonds ? playerData.diamonds : 0,
                  medalBronze: playerData.medalBronze ? playerData.medalBronze : 0,
                  medalSilver: playerData.medalSilver ? playerData.medalSilver : 0,
                  medalGold: playerData.medalGold ? playerData.medalGold : 0,
                  adminLocations: playerData.adminLocations ? playerData.adminLocations : 0,
                });
                commit('setPlayerListenerStatus', 'success');
              }
            },
            (error) => {
              console.error("Error listening to player data: ", error);
              commit('setPlayerListenerStatus', 'error');
            }
          );

          const unsubscribePlayersPrivate = onSnapshot(
            playerPrivateDocRef,
            (doc) => {
              if (doc.exists()) {
                const { lastLat, lastLon, playerPassTier, playerPassNFTID, playerPassLastClaimAt, playerPassLastClaimTx, playerPassTokenBalance } = doc.data();
                commit('setLastLocation', { lastLat, lastLon });
                commit('setPlayerPassTier', playerPassTier)
                commit('setPlayerPassNFTID', playerPassNFTID || null);
                commit('setLastClaimAt', playerPassLastClaimAt || null);
                commit('setLastClaimTx', playerPassLastClaimTx || null);
                commit('setPlayerPassTokenBalance', playerPassTokenBalance ?? 0);
                dispatch('loadNearbyLocations', { lat: lastLat, lon: lastLon });
              } else {
                commit('clearLastLocation');
                commit('clearPlayerPassTier');
                commit('clearPlayerPassNFTID');
                commit('clearPlayerPassTokenBalance');
                commit('setLastClaimAt', playerPassLastClaimAt || null);
                commit('setLastClaimTx', playerPassLastClaimTx || null);
              }
            },
            (error) => {
              console.error("Error listening to playersPrivate data: ", error);
              commit('clearLastLocation');
              commit('clearPlayerPassTier');
              commit('clearPlayerPassNFTID');
              commit('clearPlayerPassTokenBalance');
              commit('setLastClaimAt', playerPassLastClaimAt || null);
              commit('setLastClaimTx', playerPassLastClaimTx || null);
            }
          );

          commit('setPlayerListenerUnsub', () => {
            unsubscribePlayers();
            unsubscribePlayersPrivate();
          });
        } else {
          console.error("No player document found with this userId");

          // Auto-registration fallback if provider is supported
          const rawProviderId = auth.currentUser?.providerData?.[0]?.providerId || ''
          const providerMap = {
            'google.com': 'google',
            'facebook.com': 'facebook',
            'twitter.com': 'twitter'
          }
          const provider = providerMap[rawProviderId]

          if (provider) {
            try {
              const registerPlayer = httpsCallable(functions, 'players-register')
              await registerPlayer({
                uid: auth.currentUser.uid,
                email: auth.currentUser.email,
                provider,
                referral: '', // optionally pull from localStorage/cookie
                curLat: 0,
                curLon: 0,
                eventLocation: null
              })
              console.warn('[Vuex] Auto-registered new user after missing player doc.')
              return // important: wait for next listener cycle to resume
            } catch (err) {
              console.error('[Vuex] Auto-registration failed:', err.message)
            }
          } else {
            console.warn('[Vuex] Provider not eligible for auto-registration:', rawProviderId)
          }

          commit('setPlayerListenerStatus', 'error');
        }
      } catch (error) {
        console.error("Error querying player data: ", error);
        commit('setPlayerListenerStatus', 'error');
      }
    },
    async startVersionListener({ commit }) {
      try {
        const versionRef = doc(db, 'version', 'current');
        const unsubscribe = onSnapshot(
          versionRef,
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const currentVersion = docSnapshot.data().value;
              const loadedVersion = localStorage.getItem('loadedVersion');
              if (loadedVersion && loadedVersion !== currentVersion) {
                localStorage.setItem('loadedVersion', currentVersion);
                // location.reload();
              } else {
                localStorage.setItem('loadedVersion', currentVersion);
              }
              commit('setVersion', currentVersion);
            }
          },
          (error) => {
            console.error('VX: Error listening for version changes: ', error);
          }
        );
        commit('setVersionListenerUnsub', unsubscribe);
      } catch (error) {
        console.error('VX: Error setting up version listener: ', error);
      }
    },
    stopVersionListener({ state, commit }) {
      if (state.versionListenerUnsub) {
        state.versionListenerUnsub();
        commit('setVersionListenerUnsub', null);
      }
    },
    async fetchPlatformFee({ commit }) {
      try {
        const feeDoc = await getDoc(doc(db, 'ppGlobals', 'current'))
        if (feeDoc.exists()) {
          commit('setPlatformFee', feeDoc.data().platformFee)
        } else {
          commit('clearPlatformFee')
        }
      } catch (error) {
        console.error('VX: Error fetching platform fee: ', error)
        commit('clearPlatformFee')
      }
    },
    async fetchUserOnboardingStatus({ commit }, userId) {
      try {
        commit('setPlayerListenerStatus', 'loading');
        const privateQuery = query(collection(db, 'playersPrivate'), where('userId', '==', userId));
        const privateSnapshot = await getDocs(privateQuery);

        if (!privateSnapshot.empty) {
          const playerPrivateDoc = privateSnapshot.docs[0];
          const playerPrivateId = playerPrivateDoc.id;
          const playerDocRef = doc(db, 'players', playerPrivateId);
          const playerDoc = await getDoc(playerDocRef);

          if (playerDoc.exists()) {
            commit('setPlayerListenerStatus', 'success');
            commit('setUserOnboardingStatus', 'completed');
            return;
          }
        }

        const stageQuery = query(collection(db, 'playerStage'), where('userId', '==', userId));
        const stageSnapshot = await getDocs(stageQuery);

        if (!stageSnapshot.empty) {
          console.log('***ONBOARDING***')
          commit('setUserOnboardingStatus', 'onboarding');
        } else {
          console.log('***NOT_FOUND***')
          commit('setUserOnboardingStatus', 'not_found');
        }
        commit('setPlayerListenerStatus', 'success');
      } catch (error) {
        console.error('Error fetching user onboarding status:', error);
        commit('setUserOnboardingStatus', 'error');
        commit('setPlayerListenerStatus', 'error');
      }
    },
    async startMyLocationsListener({ commit, dispatch, state }, userId) {
      const start = performance.now();

      if (state.myLocationsListenerUnsub) {
        state.myLocationsListenerUnsub();
      }
      commit('setMyLocationsRawLoaded', false);

      try {
        const playersRef = collection(db, 'players');
        const playerQuery = query(playersRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(playerQuery);

        if (!querySnapshot.empty) {
          const playerDoc = querySnapshot.docs[0];
          const playerId = playerDoc.id;

          const locationQuery = query(
            collection(db, 'playerLocations'),
            where('playerId', '==', playerId),
            where('removed', '==', false)
          );

          const unsub = onSnapshot(
            locationQuery,
            (snapshot) => {
              const locations = snapshot.docs.map((doc) => doc.data().locationId);
              commit('setMyLocationsRaw', locations);
              commit('setMyLocationsRawLoaded', true);
              dispatch('startMyPlayersRawListener', userId);
              dispatch('recalculateMyPlayersRaw', userId);
              dispatch('fetchMyLocations');
              dispatch('updateEventCountsListener');
              if (state.curPlayerData?.id) {
                dispatch('updateEventCountsPrivateListener');
              }
            },
            (error) => {
              commit('setMyLocationsRawLoaded', false);
            }
          );
          commit('setMyLocationsListenerUnsub', unsub);
        } else {
          console.error('No player document found for userId:', userId);
          commit('setMyLocationsRaw', []);
          commit('setMyLocationsRawLoaded', true);
          dispatch('fetchMyLocations');
          dispatch('updateEventCountsListener');
          if (state.curPlayerData?.id) {
            dispatch('updateEventCountsPrivateListener');
          }
        }
      } catch (error) {
        console.error('Error setting up myLocations listener:', error);
        commit('setMyLocationsRawLoaded', false);
      }
      // console.log(`⏳ startMyLocationsListener: ${(performance.now() - start).toFixed(2)} ms`);

    },


    async startMyPlayersRawListener({ state, commit, dispatch }, userId) {
      const start = performance.now();
      try {
        const uid = userId || state.user?.uid;
        const key = uid ? `myPlayersRaw_${uid}` : 'myPlayersRaw';
        // Load cached myPlayersRaw from IndexedDB initially
        const cachedPlayersRaw = await get(key);
        if (cachedPlayersRaw && Array.isArray(cachedPlayersRaw.data)) {
          // console.log('⚡ Loaded myPlayersRaw from cache');
          commit('setMyPlayersRaw', cachedPlayersRaw.data);
        }

        // listeners for each favorite location
        const listeners = state.myLocationsRaw.map((locationId) => {
          return onSnapshot(
            query(collection(db, 'playerLocations'), where('locationId', '==', locationId)),
            (snapshot) => {
              const playerIds = snapshot.docs.map((doc) => doc.data().playerId);
              // console.log(`📍 Fetched playerIds for location ${locationId}:`, playerIds);
              commit('updateMyPlayersRawMap', { locationId, playerIds, userId: uid });
              dispatch('fetchMyPlayers', uid);
            },
            // (error) => console.error(`🔴 Error in listener for ${locationId}:`, error)
          );
        });

        return () => listeners.forEach((unsubscribe) => unsubscribe());
      } catch (error) {
        console.error('🔴 Error starting myPlayersRaw listener:', error);
      }
      // console.log(`⏳ startMyPlayersRawListener: ${(performance.now() - start).toFixed(2)} ms`);
    },

    async fetchMyPlayers({ state, commit, dispatch }, userId) {
      const start = performance.now();
      const cacheStart = performance.now();

      if (state.isFetchingMyPlayers) {
        state.needsMyPlayersFetch = true;
        state.pendingMyPlayersFetchUserId = userId ?? null;
        state.myPlayersLoaded = false;
        // console.log('⏭️ fetchMyPlayers skipped — already fetching');
        return;
      }

      if (state.myPlayersLoaded) {
        // console.log('⏭️ fetchMyPlayers skipped — already loaded');
        return;
      }

      state.isFetchingMyPlayers = true;

      try {
        // 1️⃣ Load from IndexedDB cache
        const uid = userId || state.user?.uid;
        const key = uid ? `myPlayers_${uid}` : 'myPlayers';
        const cachedPlayers = await get(key);
        if (cachedPlayers?.data?.length) {
          commit('setMyPlayers', cachedPlayers.data);
          // console.log(`⚡ fetchMyPlayers loaded from cache: ${(performance.now() - cacheStart).toFixed(2)} ms`);
          // Only mark loaded if we know the ID list is complete
          if (state.myPlayersRawLoaded) {
            state.myPlayersLoaded = true;
          }
        }

        // 2️⃣ Exit early if no playerIds
        if (state.myPlayersRaw.length === 0) {
          // If the ID list isn't finalized yet, just bail out and wait
          if (!state.myPlayersRawLoaded) {
            return;
          }
          commit('setMyPlayers', []);
          state.myPlayersLoaded = true;
          // console.log(`⏳ fetchMyPlayers (no players): ${(performance.now() - start).toFixed(2)} ms`);
          return;
        }

        // 3️⃣ Secure Cloud Function call
        const user = getAuth().currentUser;
        const token = user ? await user.getIdToken() : null;

        const response = await fetch(process.env.VUE_APP_CF_GET_MY_PLAYERS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ playerIds: state.myPlayersRaw }),
        });

        if (!response.ok) throw new Error('Cloud Function call failed');

        const data = await response.json();
        const myPlayers = Array.isArray(data.myPlayers) ? data.myPlayers : [];

        // console.log(`🙅‍♂️🙅‍♂️🙅‍♂️ myPlayers replaced! ${myPlayers.length} records`)


        commit('setMyPlayers', myPlayers);
        // Only mark loaded if we know the ID list won't change
        if (state.myPlayersRawLoaded) {
          state.myPlayersLoaded = true;
        }


        try {
          await set(key, { data: myPlayers });
        } catch (err) {
          console.error('🔴 Error saving myPlayers to IndexedDB:', err);
        }
      } catch (error) {
        console.error('🔴 Error fetching myPlayers:', error);
        // Treat failures as an empty result so the app can recover
        commit('setMyPlayers', []);
        state.myPlayersLoaded = true;

      } finally {
        state.isFetchingMyPlayers = false;
        const shouldRetry = state.needsMyPlayersFetch;
        const retryUserId = state.pendingMyPlayersFetchUserId;
        state.needsMyPlayersFetch = false;
        state.pendingMyPlayersFetchUserId = null;

        if (shouldRetry) {
          state.myPlayersLoaded = false;
          if (retryUserId !== null && retryUserId !== undefined) {
            dispatch('fetchMyPlayers', retryUserId);
          } else {
            dispatch('fetchMyPlayers');
          }
        }
      }

      // console.log(`⏳ fetchMyPlayers: ${(performance.now() - start).toFixed(2)} ms`);
    },

    async recalculateMyPlayersRaw({ state, commit, dispatch }, userId) {
      try {
        const uid = userId || state.user?.uid;
        const key = uid ? `myPlayersRaw_${uid}` : 'myPlayersRaw';
        if (state.myLocationsRaw.length === 0) {
          commit('setMyPlayersRaw', []);
          // Mark the ID list as finalized so fetchMyPlayers clears the player list
          commit('setMyPlayersRawLoaded', true);
          await set(key, { data: [] });
          dispatch('fetchMyPlayers', uid);
          return;
        }

        // Fetch player IDs for current locations
        const playerQueries = state.myLocationsRaw.map(locationId =>
          getDocs(query(collection(db, 'playerLocations'), where('locationId', '==', locationId)))
        );
        const results = await Promise.all(playerQueries);
        const allPlayerIds = new Set();
        results.forEach(result => {
          result.docs.forEach(doc => allPlayerIds.add(doc.data().playerId));
        });
        const updatedPlayersRaw = Array.from(allPlayerIds);

        // Update state and IndexedDB
        commit('setMyPlayersRaw', updatedPlayersRaw);
        commit('setMyPlayersLoaded', false); // force myplayers refresh
        await set(key, { data: updatedPlayersRaw });
        dispatch('fetchMyPlayers', uid); // Refresh myPlayers based on new myPlayersRaw
      } catch (error) {
        console.error('🔴 Error recalculating myPlayersRaw:', error);
      }
    },
    stopMyLocationsListener({ state, commit }) {
      if (state.myLocationsListenerUnsub) {
        state.myLocationsListenerUnsub();
        commit('setMyLocationsListenerUnsub', null);
        commit('setMyLocationsRaw', []);
        commit('setMyLocationsRawLoaded', false);
        commit('setMyLocations', []); // Clear myLocations on stop
      }
    },
    async stopMyPlayersRawListener({ state, commit }, userId) {
      state.myPlayersRawListenerUnsubs.forEach((unsub) => unsub());
      commit('setMyPlayersRawListenerUnsubs', []);
      commit('setMyPlayersRaw', []);
      commit('setMyPlayersRawMap', {});
      commit('setMyPlayersRawLoaded', false);
      state.isFetchingMyPlayers = false;
      state.needsMyPlayersFetch = false;
      state.pendingMyPlayersFetchUserId = null;
      commit('setMyPlayersLoaded', false);
      commit('setMyPlayers', []);
      const uid = userId || state.user?.uid;
      const keySuffix = uid ? `_${uid}` : '';
      try {
        await del(`myPlayers${keySuffix}`);
        await del(`myPlayersRaw${keySuffix}`);
      } catch (err) {
        console.error('🔴 Error clearing myPlayers data from IndexedDB:', err);
      }

    },
    async startMyLocationsAdminListener({ commit, state }, userId) {
      if (state.myLocationsAdminListenerUnsub) {
        state.myLocationsAdminListenerUnsub();
      }
      commit('setMyLocationsAdminRawLoaded', false);

      try {
        const playersRef = collection(db, 'players');
        const playerQuery = query(playersRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(playerQuery);

        if (!querySnapshot.empty) {
          const playerDoc = querySnapshot.docs[0];
          const playerId = playerDoc.id;

          const adminQuery = query(
            collection(db, 'locationsAdmin'),
            where('playerId', '==', playerId)
          );

          const unsub = onSnapshot(
            adminQuery,
            (snapshot) => {
              const locations = snapshot.docs.map((doc) => doc.data().locationId);
              commit('setMyLocationsAdminRaw', locations);
              commit('setMyLocationsAdminRawLoaded', true);
            },
            (error) => {
              console.error('Error in myLocationsAdminRaw listener:', error);
              commit('setMyLocationsAdminRawLoaded', false);
            }
          );
          commit('setMyLocationsAdminListenerUnsub', unsub);
        } else {
          console.error('No player document found for userId:', userId);
          commit('setMyLocationsAdminRaw', []);
          commit('setMyLocationsAdminRawLoaded', true);
        }
      } catch (error) {
        console.error('Error setting up myLocationsAdmin listener:', error);
        commit('setMyLocationsAdminRawLoaded', false);
      }
    },
    stopMyLocationsAdminListener({ state, commit }) {
      if (state.myLocationsAdminListenerUnsub) {
        state.myLocationsAdminListenerUnsub();
        commit('setMyLocationsAdminListenerUnsub', null);
        commit('setMyLocationsAdminRaw', []);
        commit('setMyLocationsAdminRawLoaded', false);
      }
    },
    // New action for myLocations
    async fetchMyLocations({ state, commit }) {
      const start = performance.now();
      // console.log('📍 fetchMyLocations via Cloud Function: START');

      // Try loading from cache first
      const cacheStart = performance.now();
      try {
        const cachedLocations = await get('myLocations');
        if (cachedLocations && Array.isArray(cachedLocations.data)) {
          commit('setMyLocations', cachedLocations.data);
          const cachedMap = {};
          cachedLocations.data.forEach(loc => { cachedMap[loc.id] = loc; });
          commit('setMyLocationsRawMap', cachedMap);
          // console.log(`⚡ fetchMyLocations loaded from cache: ${(performance.now() - cacheStart).toFixed(2)} ms`);
        }
      } catch (err) {
        console.warn('⚠️ Failed to load myLocations from cache:', err);
      }

      // Skip if no locations
      if (state.myLocationsRaw.length === 0) {
        commit('setMyLocations', []);
        commit('setMyLocationsRawMap', {});
        // console.log('📍 fetchMyLocations: skipped — empty myLocationsRaw');
        return;
      }

      try {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Not authenticated');

        const token = await user.getIdToken();

        const response = await fetch(process.env.VUE_APP_CF_GET_LOCATIONS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ locationIds: state.myLocationsRaw }),
        });

        if (!response.ok) throw new Error('Cloud Function call failed');

        const data = await response.json();
        const locations = data.locations || [];

        const locationsMap = {};
        locations.forEach(loc => {
          locationsMap[loc.id] = loc;
        });

        commit('setMyLocationsRawMap', locationsMap);
        commit('setMyLocations', locations);

        // Save to IndexedDB
        await set('myLocations', { data: locations });

        // console.log(`✅ fetchMyLocations via CF done: ${(performance.now() - start).toFixed(2)} ms`);
      } catch (error) {
        console.error('🔴 fetchMyLocations (Cloud Function) failed:', error);
      }
    },
    // Friends
    async startMyFriendsListener({ commit, state }, userId) {
      // Stop any existing listener
      if (state.myFriendsListenerUnsub) {
        state.myFriendsListenerUnsub();
      }
      commit('setMyFriendsRawLoaded', false);

      if (!userId) {
        console.warn('🚫 Cannot start myFriends listener: userId is undefined');
        commit('setMyFriendsRaw', []);
        commit('setMyFriendsRawLoaded', true);
        return;
      }


      try {
        // Fetch playerId from players collection using userId
        const playersRef = collection(db, 'players');
        const playerQuery = query(playersRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(playerQuery);

        if (!querySnapshot.empty) {
          const playerDoc = querySnapshot.docs[0];
          const playerId = playerDoc.id;

          // Query playerFriends for friends where playerId matches and friend is true
          const friendsQuery = query(
            collection(db, 'playerFriends'),
            where('userId', '==', auth.currentUser.uid),
            where('playerId', '==', playerId),
            where('friend', '==', true)
          );

          const unsub = onSnapshot(
            friendsQuery,
            (snapshot) => {
              const friends = snapshot.docs.map((doc) => doc.data().friendId);
              commit('setMyFriendsRaw', friends);
              commit('setMyFriendsRawLoaded', true);
            },
            (error) => {
              console.error('Error in myFriendsRaw listener:', error);
              commit('setMyFriendsRawLoaded', false);
            }
          );
          commit('setMyFriendsListenerUnsub', unsub);
        } else {
          console.error('No player document found for userId:', userId);
          commit('setMyFriendsRaw', []); // Empty array if no player found
          commit('setMyFriendsRawLoaded', true);
        }
      } catch (error) {
        console.error('Error setting up myFriends listener:', error);
        commit('setMyFriendsRawLoaded', false);
      }
    },
    stopMyFriendsListener({ state, commit }) {
      if (state.myFriendsListenerUnsub) {
        state.myFriendsListenerUnsub();
        commit('setMyFriendsListenerUnsub', null);
        commit('setMyFriendsRaw', []);
        commit('setMyFriendsRawLoaded', false);
      }
    },
    // Favorites
    async startMyFavoritesListener({ commit, state }, userId) {
      // Stop any existing listener
      if (state.myFavoritesListenerUnsub) {
        state.myFavoritesListenerUnsub();
      }
      commit('setMyFavoritesRawLoaded', false);

      if (!userId) {
        console.warn('🚫 Cannot start myFavorites listener: userId is undefined');
        commit('setMyFavoritesRaw', []);
        commit('setMyFavoritesRawLoaded', true);
        return;
      }

      try {
        // Fetch playerId from players collection using userId
        const playersRef = collection(db, 'players');
        const playerQuery = query(playersRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(playerQuery);

        if (!querySnapshot.empty) {
          const playerDoc = querySnapshot.docs[0];
          const playerId = playerDoc.id;

          // Query playerFavorites for Favorites where playerId matches and favorite is true
          const favoritesQuery = query(
            collection(db, 'playerFavorites'),
            where('userId', '==', auth.currentUser.uid),
            where('playerId', '==', playerId),
            where('favorite', '==', true)
          );

          const unsub = onSnapshot(
            favoritesQuery,
            (snapshot) => {
              const favorites = snapshot.docs.map((doc) => doc.data().favoriteId);
              commit('setMyFavoritesRaw', favorites);
              commit('setMyFavoritesRawLoaded', true);
            },
            (error) => {
              console.error('Error in myFavoritesRaw listener:', error);
              commit('setMyFavoritesRawLoaded', false);
            }
          );
          commit('setMyFavoritesListenerUnsub', unsub);
        } else {
          console.error('No player document found for userId:', userId);
          commit('setMyFavoritesRaw', []); // Empty array if no player found
          commit('setMyFavoritesRawLoaded', true);
        }
      } catch (error) {
        console.error('Error setting up myFavorites listener:', error);
        commit('setMyFavoritesRawLoaded', false);
      }
    },
    stopMyFavoritesListener({ state, commit }) {
      if (state.myFavoritesListenerUnsub) {
        state.myFavoritesListenerUnsub();
        commit('setMyFavoritesListenerUnsub', null);
        commit('setMyFavoritesRaw', []);
        commit('setMyFavoritesRawLoaded', false);
      }
    },
    // new actions
    async startMyEventsSummaryListener({ commit, dispatch, state }, userId) {
      const start = performance.now();
      // Stop any existing listener to prevent duplicates
      if (state.myEventsSummaryListenerUnsub) {
        state.myEventsSummaryListenerUnsub();
      }
      commit('setMyEventsSummaryLoaded', false);

      try {
        // Step 1: Query the players collection to find the player document for the userId
        const playersRef = collection(db, 'players');
        const playerQuery = query(playersRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(playerQuery);

        if (!querySnapshot.empty) {
          // Step 2: Extract playerId from the first matching document
          const playerDoc = querySnapshot.docs[0];
          const playerId = playerDoc.id;

          // Step 3: listener for playerEvents using the playerId
          const today = format(new Date(), 'yyyy-MM-dd');
          const pefRef = collection(db, 'playerEvents');
          const eventsQuery = query(
            pefRef,
            where("userId", "==", userId),
            where("playerId", "==", playerId),
            where("removed", "==", false),
            where("deleted", "==", false),
            where("startDate", ">=", today)
          );

          const unsub = onSnapshot(
            eventsQuery,
            (snapshot) => {
              // Map the snapshot to a summary of events
              const events = snapshot.docs.map((docSnapshot) => ({
                id: docSnapshot.id,
                eventId: docSnapshot.data().eventId,
                startDate: docSnapshot.data().startDate,
                startTimeMil: docSnapshot.data().startTimeMil,
              }));
              commit('setMyEventsSummary', events);
              commit('setMyEventsSummaryLoaded', true);
              // Cache the data in IndexedDB
              set('myEventsSummary', events)
                .catch((err) => console.error('🔴 Error saving myEventsSummary to IndexedDB:', err));


              // Trigger the details listener with event IDs
              const eventIds = events.map((e) => e.eventId);
              dispatch('startMyEventsDetailListener', eventIds);
            },
            (error) => {
              console.error('Error in myEventsSummary listener:', error);
              commit('setMyEventsSummaryLoaded', false);
            }
          );
          // Store the unsubscribe function to stop the listener later
          commit('setMyEventsSummaryListenerUnsub', unsub);
        } else {
          // No player document found; create empty summary
          console.error('No player document found for userId:', userId);
          commit('setMyEventsSummary', []);
          commit('setMyEventsSummaryLoaded', true);
        }
      } catch (error) {
        console.error('Error setting up myEventsSummary listener:', error);
        commit('setMyEventsSummaryLoaded', false);
      }
      // console.log(`⏳ startMyEventsSummaryListener: ${(performance.now() - start).toFixed(2)} ms`);

    },
    startMyEventsDetailListener({ commit, state }, eventIds) {
      const start = performance.now();
      // Stop existing listeners
      state.myEventsDetailListenerUnsubs.forEach(unsub => unsub());
      commit('setMyEventsDetailListenerUnsubs', []);
      commit('setMyEventsDetailLoaded', false);

      if (!eventIds || eventIds.length === 0) {
        commit('setMyEventsDetail', []);
        commit('setMyEventsDetailLoaded', true);
        return;
      }

      const eventsCollectionRef = collection(db, "events");
      const chunkSize = 10; // Firestore "in" query limit
      const unsubs = [];

      for (let i = 0; i < eventIds.length; i += chunkSize) {
        const chunk = eventIds.slice(i, i + chunkSize);
        const q = query(eventsCollectionRef, where("__name__", "in", chunk));

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const updatedEvents = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            // Merge with existing events
            let currentEvents = [...state.myEventsDetail];
            updatedEvents.forEach(updatedEvent => {
              const index = currentEvents.findIndex(e => e.id === updatedEvent.id);
              if (index !== -1) {
                currentEvents[index] = updatedEvent;
              } else {
                currentEvents.push(updatedEvent);
              }
            });
            // Remove events no longer in eventIds
            currentEvents = currentEvents.filter(e => eventIds.includes(e.id));
            commit('setMyEventsDetail', currentEvents);
            commit('setMyEventsDetailLoaded', true);
            // Cache in IndexedDB
            set('myEventsDetail', flattenForIndexedDB(currentEvents))
              .catch((err) => console.error('🔴 Error saving myEventsDetail to IndexedDB:', err));

          },
          (error) => {
            console.error("Error in myEventsDetail listener:", error);
            commit('setMyEventsDetailLoaded', false);
          }
        );
        unsubs.push(unsubscribe);
      }
      commit('setMyEventsDetailListenerUnsubs', unsubs);
      // console.log(`⏳ startMyEventsDetailListener: ${(performance.now() - start).toFixed(2)} ms`);

    },
    async loadMyEventsFromCache({ commit }) {
      const start = performance.now();

      const cachedSummary = await get('myEventsSummary'); // Using idb-keyval
      if (cachedSummary) {
        commit('setMyEventsSummary', cachedSummary);
        commit('setMyEventsSummaryLoaded', true);
      }

      const cachedDetail = await get('myEventsDetail'); // Using idb-keyval
      if (cachedDetail) {
        commit('setMyEventsDetail', cachedDetail);
        commit('setMyEventsDetailLoaded', true);
      }

      // console.log(`⏳ loadMyEventsFromCache: ${(performance.now() - start).toFixed(2)} ms`);

    },
    stopMyEventsSummaryListener({ state, commit }) {
      if (state.myEventsSummaryListenerUnsub) {
        state.myEventsSummaryListenerUnsub();
        commit('setMyEventsSummaryListenerUnsub', null);
        commit('setMyEventsSummary', []);
        commit('setMyEventsSummaryLoaded', false);
      }
    },
    stopMyEventsDetailListener({ state, commit }) {
      state.myEventsDetailListenerUnsubs.forEach(unsub => unsub());
      commit('setMyEventsDetailListenerUnsubs', []);
      commit('setMyEventsDetail', []);
      commit('setMyEventsDetailLoaded', false);
    },
    updateEventCountsListener({ commit, state }) {
      const globalStart = performance.now();

      if (state.eventCountsUnsubscribe) {
        state.eventCountsUnsubscribe();
      }
      commit('setEventCountsLoaded', false);

      const today = format(new Date(), 'yyyy-MM-dd');
      const futureDate = format(addDays(new Date(), 60), 'yyyy-MM-dd');
      const locationIds = state.myLocationsRaw || [];

      if (!state.myLocationsRawLoaded || locationIds.length === 0) {
        // commit('setEventCounts', []);
        // commit('setEventCountsLoaded', true);
        // console.log('⏳ updateEventCountsListener (no locations):', (performance.now() - globalStart).toFixed(2), 'ms');
        return;
      }

      const batchSize = 30;
      const batches = [];
      for (let i = 0; i < locationIds.length; i += batchSize) {
        batches.push(locationIds.slice(i, i + batchSize));
      }

      const unsubs = [];
      const allCounts = {};
      let completedSnapshots = 0; // ✅ Track how many batches completed

      batches.forEach((batch, index) => {
        let snapshotCount = 0;

        const eventsRef = collection(db, 'events');
        const eventsQuery = query(
          eventsRef,
          where('startDate', '>=', today),
          where('startDate', '<=', futureDate),
          where('invite', '==', false),
          where('location', 'in', batch),
          where('deleted', '==', false)
        );

        const unsub = onSnapshot(eventsQuery, (snapshot) => {
          snapshotCount++;
          const start = performance.now();

          const batchCounts = {};
          snapshot.forEach((doc) => {
            const { startDate, location, deleted } = doc.data();
            if (deleted === true) return;

            const key = `${startDate}_${location}`;
            if (!batchCounts[key]) {
              batchCounts[key] = { date: startDate, locationId: location, eventCount: 0 };
            }
            batchCounts[key].eventCount += 1;
          });

          // NEW 4/24/25 to fix random issue when counts aren't reduced in prod Before merging
          // 🔁 Always clear old keys for this batch before processing the snapshot
          Object.keys(allCounts).forEach((key) => {
            const [, locationId] = key.split('_'); // key format: `${startDate}_${location}`
            if (batch.includes(locationId)) {
              delete allCounts[key];
            }
          });


          // Merge into allCounts
          Object.keys(batchCounts).forEach((key) => {
            allCounts[key] = batchCounts[key];
          });

          const finalCounts = Object.values(allCounts);
          commit('setEventCounts', finalCounts);

          // IndexedDB write (always happens)
          set('eventCounts', finalCounts).catch((err) =>
            console.error('🔴 Error saving eventCounts to IndexedDB:', err)
          );

          // Logging
          const duration = performance.now() - start;
          // console.log(
          //   `📦 Batch ${index + 1} snapshot ${snapshotCount}: ${snapshot.size} docs in ${duration.toFixed(2)} ms`
          // );

          // ✅ Only mark as loaded after ALL batches fire
          completedSnapshots++;
          if (completedSnapshots === batches.length) {
            // console.log('⏳ All event count snapshots complete', (performance.now() - globalStart).toFixed(2), 'ms');
            commit('setEventCountsLoaded', true);
          }

        }, (error) => {
          console.error('🔴 Error in eventCounts listener:', error);
        });

        unsubs.push(unsub);
      });

      commit('setEventCountsUnsubscribe', () =>
        unsubs.forEach((unsub) => unsub())
      );

      // console.log('⏳ updateEventCountsListener END (listeners setup):', (performance.now() - globalStart).toFixed(2), 'ms');
    },
    async loadEventCountsFromCache({ commit }) {
      const start = performance.now();
      const cachedCounts = await get('eventCounts'); // Assumes IndexedDB get function
      if (cachedCounts) {
        commit('setEventCounts', cachedCounts);
        commit('setEventCountsLoaded', true);
      }
      // console.log(`⏳ loadEventCountsFromCache: ${(performance.now() - start).toFixed(2)} ms`);
    },
    updateEventCountsPrivateListener({ commit, state }) {
      const start = performance.now();
      if (state.eventCountsPrivateUnsubscribe) {
        state.eventCountsPrivateUnsubscribe();
      }
      commit('setEventCountsPrivateLoaded', false);

      const playerId = state.curPlayerData?.id;
      if (!playerId) {
        commit('setEventCountsPrivate', []);
        commit('setEventCountsPrivateLoaded', true);
        return;
      }

      const today = format(new Date(), 'yyyy-MM-dd');
      const futureDate = format(addDays(new Date(), 60), 'yyyy-MM-dd');

      const playerLocationsRefPrivate = collection(db, 'playerLocations');
      const locationsQueryPrivate = query(
        playerLocationsRefPrivate,
        where('playerId', '==', playerId),
        where('removed', '!=', true)
      );

      const allCounts = {}; // Track across all batch listeners
      const unsubs = [];

      const locationsUnsub = onSnapshot(locationsQueryPrivate, (snapshot) => {
        const locationIds = snapshot.docs.map(doc => doc.data().locationId);

        if (locationIds.length === 0) {
          commit('setEventCountsPrivate', []);
          commit('setEventCountsPrivateLoaded', true);
          return;
        }

        // Clean up old listeners before setting new ones
        unsubs.forEach(unsub => unsub());
        unsubs.length = 0;

        const batchSize = 30;
        const batches = [];
        for (let i = 0; i < locationIds.length; i += batchSize) {
          batches.push(locationIds.slice(i, i + batchSize));
        }

        batches.forEach(batch => {
          const eventsRef = collection(db, 'events');
          const eventsQuery = query(
            eventsRef,
            where('startDate', '>=', today),
            where('startDate', '<=', futureDate),
            where('invite', '==', true),
            where('location', 'in', batch)
          );

          const eventsUnsub = onSnapshot(eventsQuery, async (eventSnapshot) => {
            const batchCounts = {};

            for (const doc of eventSnapshot.docs) {
              const { startDate, location, deleted } = doc.data();
              if (deleted === true) continue;

              const { accessLevel } = await getEventAccessLevel(doc.id, playerId);
              if (accessLevel !== 'full') continue;

              const key = `${startDate}_${location}`;
              if (!batchCounts[key]) {
                batchCounts[key] = { date: startDate, locationId: location, eventCount: 0 };
              }
              batchCounts[key].eventCount += 1;
            }

            // Merge batch results into master counts
            Object.keys(batchCounts).forEach(key => {
              allCounts[key] = batchCounts[key];
            });

            const finalCounts = Object.values(allCounts);
            commit('setEventCountsPrivate', finalCounts);
            commit('setEventCountsPrivateLoaded', true);
            set('eventCountsPrivate', finalCounts).catch((err) => {
              console.error('Failed to cache eventCountsPrivate:', err);
            });

          }, (error) => {
            console.error('Error in eventCountsPrivate listener:', error);
            commit('setEventCountsPrivateLoaded', false);
          });

          unsubs.push(eventsUnsub);
        });
      }, (error) => {
        console.error('Error in locations listener for eventCountsPrivate:', error);
        commit('setEventCountsPrivateLoaded', false);
      });

      unsubs.push(locationsUnsub);
      commit('setEventCountsPrivateUnsubscribe', () => unsubs.forEach(unsub => unsub()));
      // console.log(`⏳ updateEventCountsPrivateListener: ${(performance.now() - start).toFixed(2)} ms`);


    },


    async loadEventCountsPrivateFromCache({ commit }) {
      const start = performance.now();
      const cachedCounts = await get('eventCountsPrivate');
      if (cachedCounts) {
        commit('setEventCountsPrivate', cachedCounts);
        commit('setEventCountsPrivateLoaded', true);
      }
      // console.log(`⏳ loadEventCountsPrivateFromCache: ${(performance.now() - start).toFixed(2)} ms`);

    },
    // new for close locations
    async loadNearbyLocations({ commit, state }, { lat, lon }) {
      const { fetchLocations } = useLocationsWithinDistance();
      const { haversineDistance } = useHaversine();
      const MAX_DISTANCE = 250000;
      if (!lat || !lon) {
        console.warn('📍 Skipping loadNearbyLocations — missing coords');
        return;
      }

      const distance = state.nearbyLat && state.nearbyLon
        ? haversineDistance(lat, lon, state.nearbyLat, state.nearbyLon)
        : Infinity;

      if (distance < MAX_DISTANCE) {
        // console.log(`🛑 Nearby locations still valid — ${distance.toFixed(2)} mi`);
        return;
      }

      try {
        const locations = await fetchLocations(lat, lon, MAX_DISTANCE);
        // console.log('📦 fetchLocations returned:', locations.length, 'locations');
        commit('setNearbyLocations', locations);
        commit('setNearbyLatLon', { lat, lon });
      } catch (error) {
        console.error('🔴 Failed to load nearby locations:', error);
      }
    }



  },
  getters: {
    user: (state) => state.user,
    isLoggedIn: (state) => !!state.user,
    isAppSleeping: (state) => state.isAppSleeping,
    curPlayerData: (state) => state.curPlayerData,
    version: (state) => state.version,
    userOnboardingStatus: (state) => state.userOnboardingStatus,
    playerPassTier: (state) => state.playerPassTier,
    playerPassNFTID: (state) => state.playerPassNFTID,
    playerPassLastClaimAt: (state) => state.playerPassLastClaimAt,
    playerPassLastClaimTx: (state) => state.playerPassLastClaimTx,
    platformFee: (state) => state.platformFee,
    playerPassTokenBalance: (state) => state.playerPassTokenBalance,
  },
});